import h from 'hyperscript';
import calendarIcon from '@disy/cadenza-icons/calendar.svg';
import clearIcon from '@disy/cadenza-icons/close.svg';
import chevronRightIcon from '@disy/cadenza-icons/chevron-right.svg';
import chevronLeftIcon from '@disy/cadenza-icons/chevron-left.svg';

import { createFlatpickrWithTimeLabels } from 'ui/date-picker/date-picker';
import { type FlatpickrOptions } from 'ui/flatpickr/flatpickr';
import 'ui/flatpickr/flatpickr.css';
import { createIconButton } from 'ui/button/button';
import { setupPopupButton } from 'ui/popup/popup';

import { icon } from 'cadenza/utils/icon/icon';
import { mediaSmUp } from 'cadenza/utils/media';
import {
  formatDateWithJavaFormat,
  parseAndValidateDateWithJavaFormat
} from 'cadenza/utils/date-time/date-format';
import { clamp } from 'cadenza/utils/math';
import { ChangeEvent } from 'cadenza/utils/event-util';
import { TickUnit } from 'cadenza/conditions/model/tick-unit';
import type { FlatpickrAbstractPlugin } from 'cadenza/conditions/flatpickr-abstract-plugin';
import { MonthSelectPlugin } from 'cadenza/conditions/flatpickr-month-select-plugin';
import { YearSelectPlugin } from 'cadenza/conditions/flatpickr-year-select-plugin';
import { mostFineGrainedDateFormatTickUnit } from 'cadenza/conditions/date-time-condition/date-time-condition-utils';

import './date-range-picker.css';
import i18n from './date-range-picker.properties';

const COMPONENT_NAME = 'd-date-range-picker';

export interface DateRangePickerOption {
  /** The initially pre-selected start date. */
  initialStartDate: Date;
  /** The initially pre-selected end date. */
  initialEndDate: Date;
  /** The min date the user can select as the start date. */
  minDate: Date;
  /** The max date the user can select as the end date. */
  maxDate: Date;
  /** The dates displayed are formatted according to this date format. */
  dateFormat?: string;
  /** Specifies how fine-grained the date can be selected. */
  mostFineGrainedTickUnit?: TickUnit;
  /**
   * Specifies whether the date picker calendar is displayed in a popup when the calendar icon is clicked.
   * If popup is set to false, the calendar will be toggled inline when the calendar button is clicked.
   */
  popupMode?: boolean;
}

type ActiveDateType = 'START' | 'END';

interface DateRangeChangeEventDetail {
  isStart: boolean;
  value?: Date;
}

export class DateRangeChangeEvent extends ChangeEvent<DateRangeChangeEventDetail> {

  static get type () {
    return 'change:dateRange';
  }

  constructor (detail: DateRangeChangeEventDetail) {
    super(DateRangeChangeEvent.type, { detail });
  }

}

export class DateRangePicker extends HTMLElement {

  readonly #dateFormat;
  readonly #initialStartDate;
  readonly #initialEndDate;
  readonly #minDate;
  readonly #maxDate;
  readonly #granularTickUnitOrder;
  readonly #popupMode;

  readonly #rangeInputs;
  readonly #calendar;
  readonly #calendarTitle;
  readonly #dateTimePicker;
  readonly #startDatePicker;
  readonly #endDatePicker;
  readonly #labelBetweenStartAndEnd: HTMLElement | null;

  #selectedStartDate;
  #selectedEndDate;
  #activeDateType?: ActiveDateType;

  #shortLabelText = '';
  #longLabelText = '';
  #activeDatePickerInput?: HTMLInputElement;
  #flatpickr;
  #flatpickrPlugin?: FlatpickrAbstractPlugin;

  constructor (
    {
      initialStartDate,
      initialEndDate,
      minDate,
      maxDate,
      dateFormat = 'dd.MM.yyyy',
      // Non-null assertion operator because mostFineGrainedTickUnit cannot be undefined
      mostFineGrainedTickUnit = mostFineGrainedDateFormatTickUnit(dateFormat)!,
      popupMode = false
    }: DateRangePickerOption
  ) {
    super();

    this.#dateFormat = dateFormat;
    this.#initialStartDate = initialStartDate;
    this.#initialEndDate = initialEndDate;
    this.#selectedStartDate = initialStartDate ? new Date(initialStartDate) : undefined;
    this.#selectedEndDate = initialEndDate ? new Date(initialEndDate) : undefined;

    this.#minDate = minDate;
    this.#maxDate = maxDate;

    this.#granularTickUnitOrder = TickUnit.order(mostFineGrainedTickUnit);
    this.#popupMode = popupMode;

    this.#startDatePicker = this.#createDatePicker(true);
    this.#endDatePicker = this.#createDatePicker(false);
    this.#labelBetweenStartAndEnd = this.#createLabelBetweenStartAndEnd();
    this.#rangeInputs = h(`.d-stack-h.${popupMode
      ? 'd-date-range-picker.is-popup'
      : 'space-3'}`, [
      this.#startDatePicker,
      this.#labelBetweenStartAndEnd,
      this.#endDatePicker
    ]);

    const flatPickerContainer: HTMLElement = h(`.${COMPONENT_NAME}--flatpickr-container`);
    this.#flatpickr = this.#createFlatpickr(flatPickerContainer);
    this.#calendarTitle = h(`label.${COMPONENT_NAME}--title${popupMode ? '.is-popup' : ''}`);
    this.#calendar = h(
      `.${COMPONENT_NAME}--calendar${popupMode
        ? '.is-popup'
        : ''}`, [ this.#calendarTitle, flatPickerContainer ]);
    this.#setupTimeInputs();
    this.#dateTimePicker = h('.d-stack-v.space-3', [
      this.#calendar
    ]);
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);

    if (!this.#popupMode) {
      this.append(h('.d-stack-v', [ this.#rangeInputs, this.#dateTimePicker ]));
    } else {
      this.classList.add('is-date-picker-popup');
      // this._dateTimePicker will be added as popup content
      this.append(h('.d-stack-v', [ this.#rangeInputs ]));
      mediaSmUp.addEventListener('change', this.#onMediaSmUpChanged.bind(this));
    }

    this.#updateVisibility();
  }

  disconnectedCallback () {
    mediaSmUp.removeEventListener('change', this.#onMediaSmUpChanged);
  }

  #setupTimeInputs () {
    this.#setupTimeUnitInput(this.#flatpickr.hourElement);
    this.#setupTimeUnitInput(this.#flatpickr.minuteElement);
    this.#setupTimeUnitInput(this.#flatpickr.secondElement);
  }

  #setupTimeUnitInput (input?: HTMLInputElement) {
    if (input) {
      input.disabled = !this.#getActiveDate();
      input.addEventListener('change',  () => this.#onTimeFinishedInput(input));
    }
  }

  #createLabelBetweenStartAndEnd () {
    if (!this.#popupMode) {
      if (!this.#hasTime()) {
        return h(`label.${COMPONENT_NAME}--label`, i18n('dateRangePicker.until'));
      } else {
        return null; // Displaying label does take up too much space.
      }
    }
    return h('label');
  }

  #createDatePicker (isStart: boolean): HTMLElement {
    const date = isStart ? this.#initialStartDate : this.#initialEndDate;
    const value = date ? formatDateWithJavaFormat(date, this.#dateFormat) : '';
    const input: HTMLInputElement = h('input.form-control', {
      type: 'text',
      placeholder: formatDateWithJavaFormat(isStart ? this.#minDate : this.#maxDate, this.#dateFormat),
      value,
      onfocus: () => {
        if (this.#calendar.hasAttribute('hidden')) {
          this.#updateCalendarActiveState(isStart, input);
        } else if (this.#activeDatePickerInput !== input) {
          this.#onCalendarSwitch(isStart, input);
        }
      },
      onchange: (event: Event) => this.#onDateFinishedInput((event.target as HTMLInputElement).value, isStart),
      oninput: () => {
        clearButton.hidden = !input.value;
      }
    });
    const styleClasses = [ `${COMPONENT_NAME}--clear-button` ];
    if (this.#popupMode) {
      styleClasses.push('is-popup');
    }
    const clearButton = createIconButton(icon(clearIcon),
      i18n('dateRangePicker.clear'),
      {
        variant: 'borderless',
        size: 'xs',
        styleClass: styleClasses,
        hidden: !value,
        onclick: () => {
          if (this.#calendar.hasAttribute('hidden')) {
            this.#updateCalendarActiveState(isStart, input);
          } else if (this.#activeDatePickerInput !== input) {
            this.#onCalendarSwitch(isStart, input);
          }
          this.#onDateFinishedInput('', isStart);
        }
      }
    );
    const calendarButton = createIconButton(icon(calendarIcon),
      i18n('dateRangePicker.calendarTitle'),
      {
        variant: 'borderless',
        styleClass: `${COMPONENT_NAME}--calendar-button`,
        onclick: () => this.#onCalendarSwitch(isStart, input)
      });

    if (this.#popupMode) {
      setupPopupButton(calendarButton, () => h('.d-stack-v', [ this.#dateTimePicker ]));
    }
    const datePicker: HTMLElement = h(`.${COMPONENT_NAME}--date-picker`, { tabIndex: 0 },
      [ h(`.${COMPONENT_NAME}--date-picker-input`, [ input, clearButton ]), calendarButton ]);
    const datePickerWidth =
        `calc(${this.#dateFormat.length}ch + 20px + var(--button-size-m))`;
    datePicker.style.width = datePickerWidth;
    datePicker.style.minWidth = datePickerWidth;
    return datePicker;
  }

  #createFlatpickr (appendTo: HTMLElement) {
    const flatPickrConfig: FlatpickrOptions = {
      appendTo,
      enableSeconds: this.#isTickUnitEnabled('seconds'),
      enableTime: this.#isTickUnitEnabled('hours'),
      inline: true,
      plugins: [],
      prevArrow: createIconButton(icon(chevronLeftIcon, { size: 'l' }),
        i18n('dateRangePicker.prev'),
        { variant: 'borderless' }).outerHTML,
      nextArrow: createIconButton(icon(chevronRightIcon, { size: 'l' }),
        i18n('dateRangePicker.next'),
        { variant: 'borderless' }).outerHTML,
      onChange: ([ selectedDate ]: Date[]) => {
        // it can be null because of the custom plugin
        if (selectedDate) {
          let activeDate = this.#getActiveDate();
          const oldTimeStamp = activeDate?.getTime();
          if (activeDate) {
            activeDate.setFullYear(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
          } else {
            activeDate = selectedDate;
            this.#setActiveDate(activeDate);
          }
          if (oldTimeStamp !== activeDate.getTime()) {
            this.#setDatePickerInputValue(activeDate);
            this.#onChange();
          }
        }
      }
    };

    if (this.#isYearMonth()) {
      this.#flatpickrPlugin = new MonthSelectPlugin();
      flatPickrConfig.plugins = [ this.#flatpickrPlugin.factory() ];
    } else if (this.#isYearOnly()) {
      this.#flatpickrPlugin = new YearSelectPlugin();
      flatPickrConfig.plugins = [ this.#flatpickrPlugin.factory() ];
    }
    const input = h('input.visuallyhidden');
    appendTo.append(input);
    return createFlatpickrWithTimeLabels(input, flatPickrConfig);
  }

  #onDateFinishedInput (dateString: string, isStart: boolean) {
    let adjustedDate;
    const activeDate = this.#getActiveDate();
    if (dateString) {
      const date = this.#parseAndValidateDateString(dateString);
      if (date) {
        const { earlierDate, latestDate } = this.#getAdjustedExtremeDates(isStart);
        adjustedDate = date;
        if (earlierDate && date < earlierDate) {
          adjustedDate = earlierDate;
        } else if (latestDate && date > latestDate) {
          adjustedDate = latestDate;
        }
      } else {
        adjustedDate = activeDate;
      }
    }
    this.#setDatePickerInputValue(adjustedDate);
    const oldTimeStamp = activeDate?.getTime();
    if (oldTimeStamp !== adjustedDate?.getTime()) {
      this.#setActiveDate(adjustedDate);
      this.#setFlatPickerValue(adjustedDate);
      this.#updateTimeInputs();
      this.#onChange();
    }
  }

  #onTimeFinishedInput (input: HTMLInputElement) {
    const value = clamp(input.valueAsNumber, Number(input.min), Number(input.max));
    input.value = value.toString();

    const activeDate = this.#getActiveDate();
    let previousValue;
    if (input.classList.contains('flatpickr-hour')) {
      previousValue = activeDate?.getHours();
    } else if (input.classList.contains('flatpickr-minute')) {
      previousValue = activeDate?.getMinutes();
    } else {
      previousValue = activeDate?.getSeconds();
    }

    if (value !== previousValue) {
      if (activeDate) {
        if (input.classList.contains('flatpickr-hour')) {
          activeDate.setHours(value);
        } else if (input.classList.contains('flatpickr-minute')) {
          activeDate.setMinutes(value);
        } else {
          activeDate.setSeconds(value);
        }
      }

      this.#setDatePickerInputValue(this.#getActiveDate());
      this.#onChange();
    }
  }

  #setDatePickerInputValue (date?: Date) {
    if (this.#activeDatePickerInput) {
      (this.#activeDatePickerInput.nextElementSibling as HTMLElement).hidden = !date;
      this.#activeDatePickerInput.value = date
        ? formatDateWithJavaFormat(date, this.#dateFormat)
        : '';
    }
  }

  #setFlatPickerValue (date?: Date) {
    if (date) {
      this.#flatpickr.setDate(new Date(date));
    } else {
      this.#flatpickr.clear(false);
      this.#flatpickr.jumpToDate(this.#activeDateType === 'START'
        ? this.#minDate
        : this.#maxDate);
    }
    if (this.#flatpickrPlugin) {
      this.#flatpickrPlugin.resetElements();
    }
  }

  #updateTimeInputs () {
    const activeDate = this.#getActiveDate();
    const hoursInput = this.#flatpickr.hourElement;
    if (hoursInput) {
      hoursInput.disabled = !activeDate;
      hoursInput.value = activeDate?.getHours().toString() ?? '';
    }
    const minutesInput = this.#flatpickr.minuteElement;
    if (minutesInput) {
      minutesInput.disabled = !activeDate;
      minutesInput.value = activeDate?.getMinutes().toString() ?? '';
    }
    const secondsInput = this.#flatpickr.secondElement;
    if (secondsInput) {
      secondsInput.disabled = !activeDate;
      secondsInput.value = activeDate?.getSeconds().toString() ?? '';
    }
  }

  #onChange () {
    this.dispatchEvent(new DateRangeChangeEvent({
      isStart: this.#activeDateType === 'START',
      value: this.#getActiveDate()
    }));
  }

  #onCalendarSwitch (isStart: boolean, input: HTMLInputElement) {
    const currentDatePicker = isStart ? this.#startDatePicker : this.#endDatePicker;
    if (!this.#isPressed(currentDatePicker)) {
      this.#updateCalendarActiveState(isStart, input);
      const { earlierDate, latestDate } = this.#getAdjustedExtremeDates(isStart);
      this.#flatpickr.set('minDate', earlierDate);
      this.#flatpickr.set('maxDate', latestDate);
      this.#setFlatPickerValue(this.#getActiveDate());

      this.#updateTimeInputs();

      this.#calendarTitle.textContent = i18n(`dateRangePicker.${isStart ? 'start' : 'end'}${this.#hasTime() ? 'Time' : ''}Title`);
    }
    this.#toggleCalendar(isStart);
  }

  #updateCalendarActiveState (isStart: boolean, input: HTMLInputElement) {
    this.#activeDatePickerInput = input;
    this.#activeDateType = isStart ? 'START' : 'END';
  }

  #toggleCalendar (isStart: boolean) {
    this.#startDatePicker.setAttribute('aria-pressed',
      (isStart && !this.#isPressed(this.#startDatePicker)).toString());
    this.#endDatePicker.setAttribute('aria-pressed',
      (!isStart && !this.#isPressed(this.#endDatePicker)).toString());
    this.#updateVisibility();
  }

  #updateVisibility () {
    if (!this.#popupMode) { // In popup mode, the visibility gets handled by the popup.
      const isAnyDatePickerActive = this.#isPressed(this.#startDatePicker) || this.#isPressed(this.#endDatePicker);
      this.#calendar.toggleAttribute('hidden', !isAnyDatePickerActive);
    }
  }

  #setActiveDate (date?: Date) {
    if (this.#activeDateType === 'START') {
      this.#selectedStartDate = date;
    } else {
      this.#selectedEndDate = date;
    }
    this.#updateTimeInputs();
  }

  /**
   *
   * Returns the user selected date based on _activeDateType.
   * Can be null if user has not selected any date.
   *
   * @return currentActive Date
   * @private
   */
  #getActiveDate () {
    return this.#activeDateType === 'START'
      ? this.#selectedStartDate
      : this.#selectedEndDate;
  }

  #isYearMonth () {
    return this.#granularTickUnitOrder === TickUnit.order('months');
  }

  #isYearOnly () {
    return this.#granularTickUnitOrder === TickUnit.order('years');
  }

  #getAdjustedExtremeDates (isStart: boolean) {
    const minDate = isStart ? null : this.#selectedStartDate || this.#minDate;
    const maxDate = isStart ? this.#selectedEndDate || this.#maxDate : null;
    const earlierDate = minDate ? new Date(minDate) : minDate;
    const latestDate = maxDate ? new Date(maxDate) : maxDate;
    if (this.#isYearMonth()) {
      if (earlierDate) {
        earlierDate.setDate(1);
      }
      if (latestDate) {
        latestDate.setMonth(latestDate.getMonth() + 1, 0);
      }
    } else if (this.#isYearOnly()) {
      if (earlierDate) {
        earlierDate.setMonth(0, 1);
      }
      if (latestDate) {
        latestDate.setMonth(11, 31);
      }
    }
    return { earlierDate, latestDate };
  }

  #hasTime () {
    return this.#isTickUnitEnabled('hours');
  }

  #isPressed (datePicker: HTMLElement) {
    return datePicker.getAttribute('aria-pressed') === 'true';
  }

  #isTickUnitEnabled (tickUnit: TickUnit) {
    return this.#granularTickUnitOrder <= TickUnit.order(tickUnit);
  }

  #parseAndValidateDateString (dateString: string) {
    const validDateFormats = [ this.#dateFormat ];
    if (this.#isTickUnitEnabled('seconds')) {
      validDateFormats.push('dd.MM.yyyy HH:mm:ss');
    }
    if (this.#isTickUnitEnabled('minutes')) {
      validDateFormats.push('dd.MM.yyyy HH:mm');
    }
    if (this.#isTickUnitEnabled('days')) {
      validDateFormats.push('dd.MM.yyyy');
    }

    for (const dateFormat of validDateFormats) {
      const date = parseAndValidateDateWithJavaFormat(dateString.trim(), dateFormat);
      if (date) {
        return date;
      }
    }
  }

  #onMediaSmUpChanged (event: MediaQueryListEvent | MediaQueryList) {
    if (this.#labelBetweenStartAndEnd) { // _labelBetweenStartAndEnd may not exist in non-popupMode
      this.#labelBetweenStartAndEnd.textContent = event.matches ? this.#longLabelText : this.#shortLabelText;
    }
  }

  /**
   * Updates the text between the start date picker and the end date picker.
   *
   * @param longLabelText - The text to display on large screens.
   * @param shortLabelText - The text for small screens.
   */
  updateLabelBetweenStartAndEnd (longLabelText: string, shortLabelText?: string) {
    shortLabelText = shortLabelText || longLabelText; // If short label is not set, use long label.
    this.#longLabelText = longLabelText;
    this.#shortLabelText = shortLabelText;
    this.#onMediaSmUpChanged(mediaSmUp);
  }

  /**
   * Returns the user selected dates or if not selected, the initial dates.
   *
   * @return Start and end date.
   */
  get values () {
    return {
      start: this.#selectedStartDate || this.#initialStartDate,
      end: this.#selectedEndDate || this.#initialEndDate
    };
  }

}

customElements.define(COMPONENT_NAME, DateRangePicker);
